import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Host resource console (part 19).
 *
 * Every number on this page came from a real observation of the machine that runs the API: CPU from
 * an interval measurement, memory from the platform's virtual-memory counters, disk from the mount
 * that holds the application, and load average from the kernel. The page does not compute a healthy
 * baseline and it does not smooth anything; it shows the stored sample and evaluates the operator's
 * own bands on the server, so the alert can be reproduced from the sample table.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

interface ProcessRow {
  pid: number
  name: string
  rss_bytes: number
  cpu_pct: number
}

interface Sample {
  id: string
  collected_at: string
  collected_in_ms: number
  source: string
  hostname: string
  cpu_cores: number
  cpu_pct: number
  load1: number
  load5: number
  load15: number
  mem_total_bytes: number
  mem_used_bytes: number
  mem_available_bytes: number
  swap_total_bytes: number
  swap_used_bytes: number
  disk_path: string
  disk_total_bytes: number
  disk_used_bytes: number
  disk_available_bytes: number
  uptime_seconds: number
  process_count: number
  top_processes?: ProcessRow[]
}

interface Trend {
  has_previous: boolean
  previous_at?: string
  interval_seconds: number
  cpu_delta_points: number
  memory_delta_bytes: number
  memory_delta_points: number
  disk_delta_bytes: number
  disk_delta_points: number
  load1_delta: number
}

type Level = 'ok' | 'warning' | 'critical'

interface Threshold {
  metric: string
  warning: number
  critical: number
  consecutive_breaches: number
  enabled: boolean
  level: Level
  pending_level: Level
  pending_streak: number
  last_value: number
  last_evaluated_at?: string
}

interface AlertRow {
  id: string
  metric: string
  metric_label: string
  level: 'warning' | 'critical'
  level_label: string
  status: 'firing' | 'resolved'
  status_label: string
  value: number
  threshold_value: number
  breach_streak: number
  sample_id?: string
  message: string
  started_at: string
  last_seen_at: string
  resolved_at?: string
}

interface Signal {
  metric: string
  from_level: Level
  to_level: Level
  value: number
  threshold_value: number
  pending_streak: number
  at: string
}

interface Capture {
  capture: {
    sample: Sample
    previous?: Sample
    trend: Trend
    signals: Signal[]
    open_alerts: AlertRow[]
    thresholds: Threshold[]
  }
  signals_count: number
}

interface Overview {
  latest_sample: Sample | null
  previous_sample: Sample | null
  trend: Trend
  thresholds: Threshold[]
  thresholds_stored: boolean
  open_alerts: AlertRow[]
  alert_counts: Record<string, number>
  scope_note: string
  capture_hint?: string
}

interface SampleList {
  samples: Sample[]
}

interface AlertList {
  alerts: AlertRow[]
  alert_counts: Record<string, number>
}

interface Draft {
  metric: string
  warning: number
  critical: number
  consecutive: number
  enabled: boolean
}

const OVERVIEW_KEY = ['resources-overview']
const SAMPLES_KEY = ['resources-samples']
const ALERTS_KEY = ['resources-alerts']

const METRIC_LABEL: Record<string, string> = {
  cpu_pct: 'การใช้งาน CPU',
  memory_pct: 'หน่วยความจำ',
  disk_pct: 'พื้นที่ดิสก์',
  load1: 'ค่าโหลด 1 นาที',
}

const METRIC_UNIT: Record<string, string> = {
  cpu_pct: '%',
  memory_pct: '%',
  disk_pct: '%',
  load1: '',
}

const LEVEL_CLASS: Record<Level, string> = {
  ok: 'sev-low',
  warning: 'sev-medium',
  critical: 'sev-critical',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit >= 3 ? 1 : 0)} ${units[unit]}`
}

function percentOf(part: number, total: number): number {
  if (!total || total <= 0) return 0
  return (part / total) * 100
}

function pctText(value: number): string {
  return `${value.toFixed(1)}%`
}

function metricText(metric: string, value: number): string {
  if (metric === 'load1') return value.toFixed(2)
  return pctText(value)
}

function signed(value: number, digits = 1): string {
  const text = value.toFixed(digits)
  return value > 0 ? `+${text}` : text
}

function uptimeText(seconds: number): string {
  if (!seconds || seconds <= 0) return 'ไม่ทราบ'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) return `${number(days)} วัน ${hours} ชั่วโมง`
  return `${hours} ชั่วโมง`
}

function sourceLabel(source: string): string {
  if (source === 'darwin') return 'macOS'
  if (source === 'linux') return 'Linux'
  return source
}

function draftFrom(rows: Threshold[]): Draft[] {
  return rows.map((row) => ({
    metric: row.metric,
    warning: row.warning,
    critical: row.critical,
    consecutive: row.consecutive_breaches,
    enabled: row.enabled,
  }))
}

export function Resources() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Draft[] | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const overviewQ = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api<Overview>('/v1/resources/overview'),
    staleTime: 15_000,
  })

  const samplesQ = useQuery({
    queryKey: SAMPLES_KEY,
    queryFn: () => api<SampleList>('/v1/resources/samples?limit=30'),
    staleTime: 15_000,
  })

  const alertsQ = useQuery({
    queryKey: ALERTS_KEY,
    queryFn: () => api<AlertList>('/v1/resources/alerts?limit=50'),
    staleTime: 15_000,
  })

  useEffect(() => {
    if (draft === null && overviewQ.data) {
      setDraft(draftFrom(overviewQ.data.thresholds))
    }
  }, [draft, overviewQ.data])

  const capture = useMutation({
    mutationFn: () => apiWrite<Capture>('/v1/resources/samples', 'POST', {}),
    onSuccess: (result) => {
      const s = result.capture.sample
      const signalText = result.capture.signals.length
        ? ` · เปลี่ยนแปลงระดับ ${result.capture.signals.length} รายการ`
        : ''
      setNotice(
        `เก็บตัวอย่างแล้ว · CPU ${pctText(s.cpu_pct)} · หน่วยความจำ ${pctText(
          percentOf(s.mem_used_bytes, s.mem_total_bytes),
        )} · ดิสก์ ${pctText(percentOf(s.disk_used_bytes, s.disk_used_bytes + s.disk_available_bytes))}` +
          ` · ใช้เวลา ${s.collected_in_ms} ms${signalText}`,
      )
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY })
      void qc.invalidateQueries({ queryKey: SAMPLES_KEY })
      void qc.invalidateQueries({ queryKey: ALERTS_KEY })
    },
  })

  const saveThresholds = useMutation({
    mutationFn: (rows: Draft[]) =>
      apiWrite<{ thresholds: Threshold[] }>('/v1/resources/thresholds', 'PUT', {
        thresholds: rows.map((row) => ({
          metric: row.metric,
          warning: row.warning,
          critical: row.critical,
          consecutive_breaches: row.consecutive,
          enabled: row.enabled,
        })),
      }),
    onSuccess: (result) => {
      setDraft(draftFrom(result.thresholds))
      setNotice('บันทึกเกณฑ์แล้ว · ตัวชี้วัดที่แก้จะเริ่มนับต่อเนื่องจากระดับปกติ')
      void qc.invalidateQueries({ queryKey: OVERVIEW_KEY })
      void qc.invalidateQueries({ queryKey: ALERTS_KEY })
    },
  })

  const latest = overviewQ.data?.latest_sample ?? null
  const trend = overviewQ.data?.trend
  const samples = samplesQ.data?.samples ?? []
  const alerts = alertsQ.data?.alerts ?? []
  const openAlerts = alerts.filter((row) => row.status === 'firing')

  const updateDraft = (metric: string, patch: Partial<Draft>) => {
    setDraft((rows) => rows?.map((row) => (row.metric === metric ? { ...row, ...patch } : row)) ?? rows)
  }

  const head = (
    <PageHead
      title="ทรัพยากรเครื่องแม่ข่าย"
      sub="วัด CPU หน่วยความจำ ดิสก์ และค่าโหลดจากเครื่องที่รัน API · เก็บตัวอย่างได้ทันที และแจ้งเตือนเมื่อค่าเกินเกณฑ์ต่อเนื่องตามจำนวนที่กำหนด"
      actions={
        <div className="row" style={{ gap: 10 }}>
          <button
            className="btn"
            disabled={samplesQ.isFetching || alertsQ.isFetching}
            onClick={() => {
              void overviewQ.refetch()
              void samplesQ.refetch()
              void alertsQ.refetch()
            }}
          >
            {samplesQ.isFetching ? 'กำลังอ่าน…' : 'รีเฟรช'}
          </button>
          <button className="btn btn-primary" disabled={capture.isPending} onClick={() => capture.mutate()}>
            {capture.isPending ? 'กำลังเก็บตัวอย่าง…' : 'เก็บตัวอย่างเดี๋ยวนี้'}
          </button>
        </div>
      }
    />
  )

  if (overviewQ.isLoading) {
    return (
      <>
        {head}
        <div className="stack">
          <Skel h={110} />
          <Skel h={260} />
        </div>
      </>
    )
  }

  if (overviewQ.isError || !overviewQ.data) {
    return (
      <>
        {head}
        <Empty
          title="โหลดข้อมูลทรัพยากรไม่ได้"
          body="ตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์ และตรวจว่าไมเกรชัน 0109 ถูกนำไปใช้กับฐานข้อมูลนี้แล้ว"
        />
      </>
    )
  }

  return (
    <>
      {head}

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {capture.isError ? (
        <div className="card card-pad" role="alert" style={{ marginBottom: 16 }}>
          <div className="form-error">{errorText(capture.error, 'เก็บตัวอย่างไม่สำเร็จ')}</div>
        </div>
      ) : null}

      <div className="grid-4 stagger" style={{ marginBottom: 22 }}>
        <Stat
          label="CPU"
          value={latest ? pctText(latest.cpu_pct) : '—'}
          foot={
            latest && trend?.has_previous
              ? `เปลี่ยนแปลง ${signed(trend.cpu_delta_points)} จุด จากตัวอย่างก่อนหน้า`
              : latest
                ? `${latest.cpu_cores} คอร์ · วัดในช่วงสั้น`
                : 'ยังไม่มีตัวอย่าง'
          }
        />
        <Stat
          label="หน่วยความจำ"
          value={latest ? pctText(percentOf(latest.mem_used_bytes, latest.mem_total_bytes)) : '—'}
          foot={
            latest
              ? `${formatBytes(latest.mem_used_bytes)} / ${formatBytes(latest.mem_total_bytes)}`
              : 'ยังไม่มีตัวอย่าง'
          }
        />
        <Stat
          label="พื้นที่ดิสก์"
          value={
            latest
              ? pctText(percentOf(latest.disk_used_bytes, latest.disk_used_bytes + latest.disk_available_bytes))
              : '—'
          }
          foot={
            latest
              ? `${formatBytes(latest.disk_used_bytes)} / ${formatBytes(
                  latest.disk_used_bytes + latest.disk_available_bytes,
                )} · ${latest.disk_path}`
              : 'ยังไม่มีตัวอย่าง'
          }
        />
        <Stat
          label="ค่าโหลด 1 นาที"
          value={latest ? latest.load1.toFixed(2) : '—'}
          foot={
            latest
              ? `${(latest.load1 / Math.max(latest.cpu_cores, 1)).toFixed(2)} ต่อคอร์ · ${latest.process_count} โปรเซส`
              : 'ยังไม่มีตัวอย่าง'
          }
        />
      </div>

      <div className="grid-main" style={{ marginBottom: 22 }}>
        <div className="card card-pad">
          <div className="row" style={{ alignItems: 'baseline' }}>
            <span className="card-label">ตัวอย่างล่าสุด</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              {latest
                ? `${latest.hostname || 'ไม่ทราบชื่อเครื่อง'} · ${sourceLabel(latest.source)} · เก็บเมื่อ ${thaiDateTime(latest.collected_at)}`
                : 'ยังไม่มีข้อมูล'}
            </span>
          </div>

          {!latest ? (
            <div style={{ marginTop: 14 }}>
              <Empty
                title="ยังไม่มีตัวอย่าง"
                body="กด “เก็บตัวอย่างเดี๋ยวนี้” เพื่ออ่านค่าจริงจากเครื่องนี้ ระบบจะบันทึกตัวอย่างและประเมินเกณฑ์ทั้งหมดในธุรกรรมเดียวกัน"
              />
            </div>
          ) : (
            <>
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>รายการ</th>
                      <th style={{ textAlign: 'right' }}>ค่าที่วัดได้</th>
                      <th style={{ textAlign: 'right' }}>เทียบตัวอย่างก่อน</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>CPU</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{pctText(latest.cpu_pct)}</td>
                      <td className="tnum dim" style={{ textAlign: 'right' }}>
                        {trend?.has_previous ? `${signed(trend.cpu_delta_points)} จุด` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td>หน่วยความจำที่ใช้</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>
                        {formatBytes(latest.mem_used_bytes)}
                        <span className="dim" style={{ fontSize: 10.5 }}> / {formatBytes(latest.mem_total_bytes)}</span>
                      </td>
                      <td className="tnum dim" style={{ textAlign: 'right' }}>
                        {trend?.has_previous ? `${signed(trend.memory_delta_bytes / (1024 * 1024 * 1024))} GB` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td>ดิสก์ที่ใช้</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>
                        {formatBytes(latest.disk_used_bytes)}
                        <span className="dim" style={{ fontSize: 10.5 }}>
                          {' '}/ {formatBytes(latest.disk_used_bytes + latest.disk_available_bytes)}
                        </span>
                      </td>
                      <td className="tnum dim" style={{ textAlign: 'right' }}>
                        {trend?.has_previous ? `${signed(trend.disk_delta_bytes / (1024 * 1024))} MB` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td>ค่าโหลด 1 / 5 / 15 นาที</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>
                        {latest.load1.toFixed(2)} / {latest.load5.toFixed(2)} / {latest.load15.toFixed(2)}
                      </td>
                      <td className="tnum dim" style={{ textAlign: 'right' }}>
                        {trend?.has_previous ? `${signed(trend.load1_delta, 2)}` : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td>Swap ที่ใช้</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>
                        {formatBytes(latest.swap_used_bytes)}
                        <span className="dim" style={{ fontSize: 10.5 }}> / {formatBytes(latest.swap_total_bytes)}</span>
                      </td>
                      <td className="dim" style={{ textAlign: 'right' }}>—</td>
                    </tr>
                    <tr>
                      <td>เวลาเปิดเครื่อง</td>
                      <td style={{ textAlign: 'right' }}>{uptimeText(latest.uptime_seconds)}</td>
                      <td className="dim" style={{ textAlign: 'right' }}>{latest.collected_in_ms} ms</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.8 }}>
                หน่วยความจำนับจาก active + wired + compressor ตามการนับของระบบปฏิบัติการ ·
                ดิสก์วัดที่ {latest.disk_path} และใช้พื้นที่ว่างที่เขียนได้จริงเป็นตัวหาร
              </div>

              {latest.top_processes && latest.top_processes.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                  <div className="card-label">โปรเซสที่ใช้หน่วยความจำมากที่สุด</div>
                  <div className="table-wrap" style={{ marginTop: 8 }}>
                    <table className="table data">
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'right' }}>PID</th>
                          <th>ชื่อ</th>
                          <th style={{ textAlign: 'right' }}>RSS</th>
                          <th style={{ textAlign: 'right' }}>CPU สะสม</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latest.top_processes.slice(0, 5).map((proc) => (
                          <tr key={proc.pid}>
                            <td className="tnum" style={{ textAlign: 'right' }}>{proc.pid}</td>
                            <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {proc.name}
                            </td>
                            <td className="tnum" style={{ textAlign: 'right' }}>{formatBytes(proc.rss_bytes)}</td>
                            <td className="tnum" style={{ textAlign: 'right' }}>{proc.cpu_pct.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="card card-pad">
          <div className="row" style={{ alignItems: 'baseline' }}>
            <span className="card-label">เกณฑ์แจ้งเตือน</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              {overviewQ.data.thresholds_stored ? 'บันทึกแล้ว' : 'ค่าเริ่มต้น (ยังไม่บันทึก)'}
            </span>
          </div>
          <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, margin: '10px 0 14px' }}>
            ระดับจะเปลี่ยนเมื่อค่าเกินเกณฑ์ติดกันตามจำนวนตัวอย่างที่กำหนด ทั้งขาเข้าและขาออก ·
            ค่าโหลดเป็นค่าสัมบูรณ์ ไม่ใช่เปอร์เซ็นต์
          </p>

          {draft === null ? (
            <Skel h={180} />
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              {draft.map((row) => (
                <div key={row.metric} className="card card-pad" style={{ padding: 12 }}>
                  <div className="row" style={{ alignItems: 'baseline' }}>
                    <span style={{ fontSize: 12.5 }}>{METRIC_LABEL[row.metric] ?? row.metric}</span>
                    <span className="dim" style={{ fontSize: 10.5, marginLeft: 6 }}>
                      {row.metric}{METRIC_UNIT[row.metric] ? ` · ${METRIC_UNIT[row.metric]}` : ''}
                    </span>
                    <label className="row" style={{ marginLeft: 'auto', gap: 6, fontSize: 11 }}>
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(event) => updateDraft(row.metric, { enabled: event.target.checked })}
                      />
                      เปิดใช้
                    </label>
                  </div>
                  <div className="grid-3" style={{ marginTop: 10, gap: 8 }}>
                    <div className="field" style={{ marginTop: 0 }}>
                      <label htmlFor={`th-warning-${row.metric}`}>เตือน</label>
                      <input
                        id={`th-warning-${row.metric}`}
                        className="input"
                        type="number"
                        min={0}
                        step="0.1"
                        value={row.warning}
                        onChange={(event) => updateDraft(row.metric, { warning: Number(event.target.value) })}
                      />
                    </div>
                    <div className="field" style={{ marginTop: 0 }}>
                      <label htmlFor={`th-critical-${row.metric}`}>วิกฤต</label>
                      <input
                        id={`th-critical-${row.metric}`}
                        className="input"
                        type="number"
                        min={0}
                        step="0.1"
                        value={row.critical}
                        onChange={(event) => updateDraft(row.metric, { critical: Number(event.target.value) })}
                      />
                    </div>
                    <div className="field" style={{ marginTop: 0 }}>
                      <label htmlFor={`th-streak-${row.metric}`}>ต่อเนื่อง</label>
                      <input
                        id={`th-streak-${row.metric}`}
                        className="input"
                        type="number"
                        min={1}
                        max={100}
                        step="1"
                        value={row.consecutive}
                        onChange={(event) => updateDraft(row.metric, { consecutive: Number(event.target.value) })}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                className="btn btn-primary"
                disabled={saveThresholds.isPending}
                onClick={() => draft && saveThresholds.mutate(draft)}
              >
                {saveThresholds.isPending ? 'กำลังบันทึก…' : 'บันทึกเกณฑ์'}
              </button>
              {saveThresholds.isError ? (
                <div className="form-error">{errorText(saveThresholds.error, 'บันทึกเกณฑ์ไม่สำเร็จ')}</div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="card-label">การแจ้งเตือน</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            กำลังแจ้ง {number(overviewQ.data.alert_counts.firing ?? 0)} · สิ้นสุดแล้ว{' '}
            {number(overviewQ.data.alert_counts.resolved ?? 0)} · เปิดอยู่ตอนนี้ {number(openAlerts.length)}
          </span>
        </div>

        {alerts.length === 0 ? (
          <div style={{ marginTop: 12 }}>
            <Empty
              title="ยังไม่มีการแจ้งเตือน"
              body="เมื่อค่าที่วัดได้เกินเกณฑ์ติดต่อกันตามจำนวนที่กำหนด ระบบจะเปิดรายการแจ้งเตือนพร้อมค่าที่พบและเกณฑ์ที่ใช้ตัดสิน"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>ระดับ</th>
                  <th>ตัวชี้วัด</th>
                  <th style={{ textAlign: 'right' }}>ค่าที่พบ / เกณฑ์</th>
                  <th style={{ textAlign: 'right' }}>ต่อเนื่อง</th>
                  <th>เริ่มเมื่อ</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className={`sev ${row.level === 'critical' ? 'sev-critical' : 'sev-medium'}`}>
                        <i className="sev-dot" />
                        {row.level_label}
                      </span>
                    </td>
                    <td>
                      <div>{row.metric_label}</div>
                      <div className="dim" style={{ fontSize: 10.5, maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.message}
                      </div>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right' }}>
                      {metricText(row.metric, row.value)}
                      <span className="dim" style={{ fontSize: 10.5 }}> / {metricText(row.metric, row.threshold_value)}</span>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{row.breach_streak}</td>
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(row.started_at)}</td>
                    <td>
                      <span className={`sev ${row.status === 'firing' ? LEVEL_CLASS[row.level] : 'sev-low'}`}>
                        <i className="sev-dot" />
                        {row.status_label}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad">
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="card-label">ประวัติตัวอย่าง</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            ใหม่สุดก่อน · {number(samples.length)} รายการ
          </span>
        </div>

        {samples.length === 0 ? (
          <div style={{ marginTop: 12 }}>
            <Empty title="ยังไม่มีประวัติ" body="ตัวอย่างแรกจะปรากฏที่นี่หลังกดเก็บตัวอย่าง" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th style={{ textAlign: 'right' }}>CPU</th>
                  <th style={{ textAlign: 'right' }}>หน่วยความจำ</th>
                  <th style={{ textAlign: 'right' }}>ดิสก์</th>
                  <th style={{ textAlign: 'right' }}>Load 1m</th>
                  <th style={{ textAlign: 'right' }}>ใช้เวลา</th>
                  <th>แหล่งที่มา</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((row) => (
                  <tr key={row.id}>
                    <td>{thaiDateTime(row.collected_at)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{pctText(row.cpu_pct)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>
                      {pctText(percentOf(row.mem_used_bytes, row.mem_total_bytes))}
                    </td>
                    <td className="tnum" style={{ textAlign: 'right' }}>
                      {pctText(percentOf(row.disk_used_bytes, row.disk_used_bytes + row.disk_available_bytes))}
                    </td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{row.load1.toFixed(2)}</td>
                    <td className="dim tnum" style={{ textAlign: 'right' }}>{row.collected_in_ms} ms</td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {sourceLabel(row.source)} · {row.hostname || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
